import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Flex, Box } from 'rebass/styled-components';
import PropTypes from 'prop-types';
import { Loader, MessageBlock } from '@getro/rombo';
import UserSchema from 'schemas/user';
import strings from 'strings';
import toast from 'react-hot-toast';
import useDebounce from 'hooks/useDebounce';
import { useTierGating } from 'hooks/useTierGating';
import { activeNetworkSelector } from 'redux/selectors';
import { AdminDataTable } from './AdminDataTable';
import TeamMembersFilters from './components/TeamMembersFilters';
import { EmptyView } from './components/EmptyView';
import { useFilteredAdmins, EMPTY_FILTERS } from './hooks/useFilteredAdmins';
import RemoveAdminModal from '../adminPanel/removeAdminModal';

export const AdminTable = ({
  onRemoveAdmin = null,
  onRemoveAdminClick = null,
  onEditRoleClick = null,
  onResendInvitation = () => {},
  admins = [],
  currentUser,
  isResendingInvitation = false,
  isDeletingAdmin = false,
  loadingAdminsError,
  isLoadingAdmins,
}) => {
  const activeNetwork = useSelector(activeNetworkSelector);
  const canManageAdmins = !!activeNetwork?.isManager;
  const { isCustomer } = useTierGating({ network: activeNetwork });
  const urlParams = new URLSearchParams(window.location.search);
  const inviteModalOpen = urlParams.has('invite-admin') || urlParams.has('invite-manager');
  const [payload, setActionPayload] = useState(inviteModalOpen ? { action: 'invite' } : null);
  const [userToResendInvitation, setUserToResendInvitation] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const trimmedSearch = filters.search.trim();
  const debouncedSearch = useDebounce(trimmedSearch, 300);
  // Clearing the search should reset the list instantly instead of waiting out
  // the debounce, so an empty input applies immediately.
  const appliedSearch = trimmedSearch === '' ? '' : debouncedSearch;
  const appliedFilters = useMemo(
    () => ({
      search: appliedSearch,
      inviteStatus: filters.inviteStatus,
      // Role/contacts/LinkedIn controls are hidden on non-customer networks;
      // don't apply their (possibly stale) values when they aren't rendered.
      role: isCustomer ? filters.role : '',
      contactsShared: isCustomer ? filters.contactsShared : '',
      linkedin: isCustomer ? filters.linkedin : '',
    }),
    [appliedSearch, filters.inviteStatus, filters.role, filters.contactsShared, filters.linkedin, isCustomer],
  );
  const filteredAdmins = useFilteredAdmins(admins ?? [], appliedFilters);
  const hasActiveFilters = Object.values(appliedFilters).some((value) => value !== '');

  const handleFilterChange = (name, value) => setFilters((prev) => ({ ...prev, [name]: value }));

  const handleSetPayload = (data, adminAction) => {
    if (adminAction === 'remove' && onRemoveAdminClick?.(data)) return;
    if (adminAction === 'edit-role' && onEditRoleClick?.(data)) return;

    setActionPayload({
      data,
      action: adminAction,
    });
  };

  if (loadingAdminsError) return <MessageBlock data-testid="fetch-error">{strings.genericError}</MessageBlock>;

  if (isLoadingAdmins)
    return (
      <Flex mt="40px" justifyContent="center" width="100%" sx={{ overflow: 'hidden' }}>
        <Loader />
      </Flex>
    );

  return (
    <>
      <Box m="0 auto">
        <TeamMembersFilters filters={filters} onChange={handleFilterChange} isCustomer={isCustomer} />
        <AdminDataTable
          admins={filteredAdmins}
          hasActiveFilters={hasActiveFilters}
          emptyView={<EmptyView hasActiveFilters={hasActiveFilters} />}
          onRemoveAdmin={canManageAdmins && onRemoveAdmin ? handleSetPayload : null}
          onResendInvitation={async (user) => {
            if (user.role !== 'ally' && !canManageAdmins) return;
            setUserToResendInvitation(user);
            const result = await onResendInvitation(user);
            if (result?.error) {
              setUserToResendInvitation(null);
              return;
            }
            toast('Invite resent', {
              icon: 'success',
              id: 'resend-network-invite',
            });
            setUserToResendInvitation(null);
          }}
          isResendingInvitation={isResendingInvitation}
          onUpdateRole={onEditRoleClick ? handleSetPayload : null}
          userToResendInvitation={userToResendInvitation}
        />
      </Box>
      {canManageAdmins && onRemoveAdmin && (
        <RemoveAdminModal
          user={payload?.data}
          currentUser={currentUser}
          network={activeNetwork}
          isSubmitting={isDeletingAdmin}
          modalOpen={payload?.action === 'remove'}
          onCancel={() => setActionPayload(null)}
          onDeleteAdmin={async () => {
            const result = await onRemoveAdmin(payload.data);
            if (result?.error) return;
            setActionPayload(null);
          }}
        />
      )}
    </>
  );
};

AdminTable.propTypes = {
  isDeletingAdmin: PropTypes.bool,
  isResendingInvitation: PropTypes.bool,
  currentUser: UserSchema.isRequired,
  admins: PropTypes.array,
  onRemoveAdmin: PropTypes.func,
  onRemoveAdminClick: PropTypes.func,
  onEditRoleClick: PropTypes.func,
  onResendInvitation: PropTypes.func,
  loadingAdminsError: PropTypes.bool.isRequired,
  isLoadingAdmins: PropTypes.bool.isRequired,
};

AdminTable.defaultProps = {
  isDeletingAdmin: false,
  isResendingInvitation: false,
  admins: [],
  onRemoveAdmin: null,
  onRemoveAdminClick: null,
  onEditRoleClick: null,
  onResendInvitation: () => {},
};
